import { FC, useEffect } from 'react';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { Checkbox, FormControlLabel } from '@mui/material';
import { isSameDay } from 'date-fns';
import { useSelector, useDispatch } from '../../../shared/hooks/redux';
import { ChooseDate } from '../../../components/ChooseDate';
import { ChooseDeliveryTime } from '../../../components/ChooseDeliveryTime';
import { Header } from '../../../components/header';
import { InnsendingLinje } from '../../../components/Header4';
import { futureDateIsNotSelected } from '../../../core/helpers/functions';
import {
  setDeliveryDate,
  setIsEarlyDelivery,
  setDeliveryTimeType,
  setStartTime,
  setEndTime,
  setIsImportantDeadline,
  resetStates,
  setDaytimeDelivery,
} from '../../../core/redux/haulOrderState';
import { CancelButton } from '../../../shared/components/CancelButton';
import { Content } from '../../../shared/components/Content';
import { PageUnauthenticated } from '../../../shared/components/page';
import { useSetDeliveryDateToNextWorkingDay } from '../../DeliveryDate';
import { translateOrderType } from '../../../core/helpers/translate';

export const HaulDetailsPage: FC = () => {
  const navigate = useNavigate();
  const {
    type,
    projectFrom,
    projectTo,
    deliveryDate,
    startTime,
    endTime,
    deliveryTimeType,
    userDefinedDeliveryDate,
    isEarlyDelivery,
    isImportantDeadLine,
    daytimeDelivery,
  } = useSelector((s) => s.haulOrderState);
  const dispatch = useDispatch();

  const setDeliveryDateToNextWorkingDay = useSetDeliveryDateToNextWorkingDay('haul');

  useEffect(() => {
    if (type !== null) return;
    if (projectFrom !== null) return;
    if (projectTo !== null) return;
    navigate('/maskinslep');
  }, [type, projectFrom, projectTo]);

  if (projectTo === null) {
    return null;
  }

  // all conditions should be false to move on to next page
  const disableNextPage = () => {
    const checkTimeSelected = deliveryTimeType.type === 5
      && (startTime === null || endTime === null);
    if (deliveryTimeType.type === 5) {
      return (
        futureDateIsNotSelected(deliveryDate, userDefinedDeliveryDate)
        || checkTimeSelected
      );
    }
    return (
      futureDateIsNotSelected(deliveryDate, userDefinedDeliveryDate)
      || checkTimeSelected);
  };

  return (
    <PageUnauthenticated className="leveringsdetaljer-page parent-pos">

      <Header>{type ? translateOrderType(type) : 'Maskinslep'}</Header>
      <Content>
        <InnsendingLinje text="Steg 2 - Leveringsdetaljer" styling="page-item" />

        <InnsendingLinje text="Leveringsdato" styling="page-item" />

        <div className="page-item">
          <Button
            onClick={setDeliveryDateToNextWorkingDay}
            variant={deliveryDate.type === 1 ? 'contained' : 'outlined'}
            size="medium"
          >
            Neste arbeidsdag
          </Button>
          <Button
            onClick={() => dispatch(setDeliveryDate({ type: 2, day: userDefinedDeliveryDate }))}
            variant={deliveryDate.type === 2 ? 'contained' : 'outlined'}
            size="medium"
          >
            Velg dato
          </Button>
          <Button
            onClick={() => dispatch(setDeliveryDate({ type: 3, day: new Date() }))}
            variant={deliveryDate.type === 3 ? 'contained' : 'outlined'}
            size="medium"
          >
            I dag
          </Button>
        </div>
        <ChooseDate state="haul" />

        <FormControlLabel
          disabled={(deliveryDate.day && isSameDay(deliveryDate.day, new Date())) || false}
          className="page-item pt10"
          control={(
            <Checkbox
              checked={isEarlyDelivery}
              onClick={() => dispatch(setIsEarlyDelivery(!isEarlyDelivery))}
            />
              )}
          label="Kan leveres dagen før"
        />
        <InnsendingLinje text="Leveringstidspunkt" styling="page-item" />

        <div className="page-item">
          <Button
            onClick={() => dispatch(setDeliveryTimeType({ type: 1, time: 'I løpet av dagen' }))}
            variant={deliveryTimeType.type === 1 ? 'contained' : 'outlined'}
            size="medium"
          >
            I løpet av dagen
          </Button>
          <Button
            onClick={() => dispatch(setDeliveryTimeType({ type: 2, time: 'Morgen' }))}
            variant={deliveryTimeType.type === 2 ? 'contained' : 'outlined'}
            size="medium"
          >
            Morgen
          </Button>
          <Button
            onClick={() => dispatch(setDeliveryTimeType({ type: 3, time: 'Før mat' }))}
            variant={deliveryTimeType.type === 3 ? 'contained' : 'outlined'}
            size="medium"
          >
            Før mat
          </Button>
          <Button
            onClick={() => dispatch(setDeliveryTimeType({ type: 4, time: 'Etter mat' }))}
            variant={deliveryTimeType.type === 4 ? 'contained' : 'outlined'}
            size="medium"
          >
            Etter mat
          </Button>
          <Button
            onClick={() => dispatch(setDeliveryTimeType({ type: 6, time: 'Etter arbeidstid' }))}
            variant={deliveryTimeType.type === 6 ? 'contained' : 'outlined'}
            size="medium"
          >
            Etter arbeidstid
          </Button>
          <Button
            onClick={() => dispatch(setDeliveryTimeType({ type: 5, time: 'Egendefinert' }))}
            variant={deliveryTimeType.type === 5 ? 'contained' : 'outlined'}
            size="medium"
          >
            Egendefinert
          </Button>
        </div>
        {deliveryTimeType.type === 5 && (
        <ChooseDeliveryTime
          startTime={startTime}
          onStartTimeChange={(v) => dispatch(setStartTime(v))}
          endTime={endTime}
          onEndTimeChange={(v) => dispatch(setEndTime(v))}
          isImportantDeadLine={isImportantDeadLine}
          onIsImportantDeadlineChange={(v) => dispatch(setIsImportantDeadline(v))}
          deliveryDate={deliveryDate.day}
        />
        )}
        <FormControlLabel
          className="page-item pt10"
          control={(
            <Checkbox
              checked={daytimeDelivery}
              onClick={() => dispatch(setDaytimeDelivery(!daytimeDelivery))}
            />
              )}
          title="07:00 - 15:00"
          label="Kan tas på dagtid"
        />

        <div className="bottom-inner">
          <div className="bottom-element">
            <Button
              className="left-element"
              onClick={() => navigate(-1)}
              variant="contained"
              size="large"
            >
              {'<'}
            </Button>
            <CancelButton confirm={() => {
              dispatch(resetStates());
              return navigate('/maskinslep');
            }}
            />
            <Button
              className="right-element"
              onClick={() => navigate('./../tilleggsdetaljer')}
              variant="contained"
              size="large"
              disabled={disableNextPage()}
            >
              {'>'}
            </Button>
          </div>
        </div>
      </Content>
    </PageUnauthenticated>
  );
};
